import type { RawImage, ResizeTarget } from "./codec";
import { decodeWithCodec } from "./decoders";
import {
  isCompressibleRasterImage,
  mayCarryAlpha,
  toCompressedFile,
} from "./formats";
import { runEncode } from "./runEncode";

export { isCompressibleRasterImage };

export const IMAGE_COMPRESSION_MAX_SIZE_MB = {
  exhibitorLogo: 0.3,
  organiserLogo: 0.3,
  supplierLogo: 0.3,
  showLogo: 0.3,
  certificate: 0.3,
  product: 2,
  customField: 2,
  messagingBoard: 2,
  banner: 2,
  showCardImage: 2,
  taskDescription: 1,
  showInfo: 1,
  taskComment: 2,
  taskReview: 2,
  orderReview: 2,
  orderNotes: 2,
} as const;

export const LOGO_MAX_DIMENSION_PX = 450;

type CompressImageOptions = {
  maxWidthOrHeight?: number;
};

/**
 * Browsers silently hand back a blank canvas past a certain pixel area — the
 * cap is lowest on iOS Safari. A 48-megapixel phone photo clears it easily, so
 * anything larger is scaled down before it is drawn, whether or not the caller
 * asked for a resize.
 */
const MAX_CANVAS_PIXELS = 16_777_216;

/**
 * How much bigger than the final target the decode canvas may be. Lanczos from
 * twice the target is indistinguishable from Lanczos from the full original,
 * and it keeps a 6000 px logo upload from being carried through memory as
 * ~150 MB of RGBA — twice over, since the pixels are also cloned into the
 * Worker — on the way to becoming a 450 px thumbnail.
 */
const PRE_RESIZE_HEADROOM = 2;

const BYTES_PER_MB = 1024 * 1024;

/**
 * How big the decode canvas may be. Neither of these is the caller's resize —
 * the quality-critical downscale is Lanczos and happens later, in the codec.
 * This only decides how many pixels have to be held in the meantime.
 */
const getCanvasSize = (
  width: number,
  height: number,
  resize?: ResizeTarget
): ResizeTarget => {
  const headroom = resize
    ? (Math.max(resize.width, resize.height) * PRE_RESIZE_HEADROOM) /
      Math.max(width, height)
    : 1;

  let scale = Math.min(1, headroom);

  const pixels = width * scale * (height * scale);
  if (pixels > MAX_CANVAS_PIXELS) {
    scale *= Math.sqrt(MAX_CANVAS_PIXELS / pixels);
  }

  if (scale >= 1) {
    return { width, height };
  }

  // Rounded down rather than to nearest: rounding both sides up can put the
  // area back over the very cap this exists to respect.
  return {
    width: Math.max(1, Math.floor(width * scale)),
    height: Math.max(1, Math.floor(height * scale)),
  };
};

/**
 * The caller's `maxWidthOrHeight` applied to the longest side, so the aspect
 * ratio is preserved exactly. Returns nothing when no resize is wanted or the
 * image is already within the cap — an upload smaller than the recommended
 * size is never blown up to reach it.
 */
const getResizeTarget = (
  width: number,
  height: number,
  maxWidthOrHeight?: number
): ResizeTarget | undefined => {
  const longestSide = Math.max(width, height);

  if (!maxWidthOrHeight || longestSide <= maxWidthOrHeight) {
    return undefined;
  }

  const scale = maxWidthOrHeight / longestSide;

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

/**
 * Native decode first, WASM codec second.
 *
 * `createImageBitmap` is faster, free of bundle weight, and reads formats no
 * codec here can (BMP, and HEIC on Safari); `from-image` applies the EXIF
 * rotation a phone camera records, so portrait photos don't come out on their
 * side. It throws on a format the browser doesn't know — JPEG XL and QOI
 * always, AVIF and WebP on older browsers — and only then does a codec load.
 */
// TypeScript 4.9's lib.dom still types `ImageOrientation` as
// `"flipY" | "none"`; `"from-image"` was added to the spec later and is what
// every browser we support actually implements.
const EXIF_AWARE_DECODE = ({
  imageOrientation: "from-image",
} as unknown) as ImageBitmapOptions;

const createNativeBitmap = async (file: File): Promise<ImageBitmap> => {
  try {
    return await createImageBitmap(file, EXIF_AWARE_DECODE);
  } catch {
    // A browser that predates `from-image` rejects the unknown enum value
    // outright, which says nothing about whether it can read the file. Retry
    // bare and accept whatever rotation it applies by default.
    return createImageBitmap(file);
  }
};

const decodeToBitmap = async (file: File): Promise<ImageBitmap> => {
  try {
    return await createNativeBitmap(file);
  } catch (error) {
    const imageData = await decodeWithCodec(file);

    if (!imageData) {
      throw error;
    }

    return createImageBitmap(imageData);
  }
};

/**
 * Decodes to plain pixels, no larger than the encode step will actually use.
 */
const decodeForEncode = async (
  file: File,
  maxWidthOrHeight?: number
): Promise<{ image: RawImage; resize?: ResizeTarget }> => {
  const bitmap = await decodeToBitmap(file);

  try {
    const resize = getResizeTarget(
      bitmap.width,
      bitmap.height,
      maxWidthOrHeight
    );
    const { width, height } = getCanvasSize(
      bitmap.width,
      bitmap.height,
      resize
    );

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Could not get a 2d canvas context");
    }

    context.drawImage(bitmap, 0, 0, width, height);
    const { data } = context.getImageData(0, 0, width, height);

    return { image: { data, width, height }, resize };
  } finally {
    bitmap.close();
  }
};

/**
 * Whether the image carries any see-through pixel, which decides between PNG
 * and JPEG below. Exact rather than sampled: the pixels are already decoded at
 * this point, so there is nothing to save by guessing.
 */
const hasTransparency = ({ data }: RawImage): boolean => {
  for (let index = 3; index < data.length; index += 4) {
    if (data[index] < 255) {
      return true;
    }
  }

  return false;
};

const compressWithCodecs = async (
  file: File,
  maxSizeMB: number,
  { maxWidthOrHeight }: CompressImageOptions
): Promise<File | null> => {
  try {
    const { image, resize } = await decodeForEncode(file, maxWidthOrHeight);

    const { buffer, mimeType } = await runEncode({
      image,
      resize,
      transparent: mayCarryAlpha(file) && hasTransparency(image),
      maxBytes: maxSizeMB * BYTES_PER_MB,
    });

    return toCompressedFile(file, buffer, mimeType);
  } catch {
    return null;
  }
};

const compressWithCanvas = async (
  file: File,
  maxSizeMB: number,
  { maxWidthOrHeight }: CompressImageOptions
): Promise<File | null> => {
  try {
    // Imported here so the fallback's own dependency stays out of the bundle
    // until something actually needs it.
    const { legacyCompress } = await import("./legacyCompress");
    const compressed = await legacyCompress(file, maxSizeMB, maxWidthOrHeight);

    return toCompressedFile(file, compressed, compressed.type);
  } catch {
    return null;
  }
};

/**
 * Compresses an image to `maxSizeMB`, converting it to JPEG or PNG.
 *
 * Three tiers, each a fallback for the one before:
 * 1. the WASM codecs (MozJPEG / OxiPNG / Lanczos), which produce the smallest
 *    and sharpest result;
 * 2. `browser-image-compression` on plain canvas, for browsers or policies
 *    where WebAssembly can't run at all;
 * 3. the original file, untouched.
 *
 * The codecs could also emit WebP, AVIF, JPEG XL and QOI, and deliberately
 * don't: every consumer downstream has to be able to read the result.
 * `@react-pdf/image` (task and certificate PDFs) decodes only JPEG, PNG and
 * SVG; custom-field files flow into external integrations; images in emails
 * reach arbitrary mail clients. JPEG and PNG are the only formats all of them
 * handle. The input side is the opposite — see
 * `COMPRESSIBLE_IMAGE_MIME_TYPES`, which takes everything either the browser
 * or a codec can read.
 *
 * The format is chosen by content, not by upload location:
 * - transparent → PNG, since JPEG would flatten the alpha to black;
 * - otherwise → JPEG, which is far smaller than PNG for photographic content.
 *
 * `maxSizeMB` is a target, not a guarantee. A transparent image is encoded
 * losslessly, so when PNG alone can't reach the target the only remaining
 * lever is resolution — pass `maxWidthOrHeight` for those call sites (logos),
 * otherwise resolution is preserved. That cap applies to the longest side and
 * keeps the aspect ratio exactly; an image already within it is left alone
 * rather than blown up to reach it.
 *
 * Never throws: on failure, or when the result isn't actually smaller, the
 * original file is returned unchanged.
 */
export const compressImage = async (
  file: File,
  maxSizeMB: number,
  options: CompressImageOptions = {}
): Promise<File> => {
  if (!isCompressibleRasterImage(file)) {
    return file;
  }

  const compressed =
    (await compressWithCodecs(file, maxSizeMB, options)) ??
    (await compressWithCanvas(file, maxSizeMB, options));

  if (!compressed || compressed.size >= file.size) {
    return file;
  }

  return compressed;
};
